import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { MCMCombobox } from '@/components/ui/MCMCombobox';
import { Switch } from '@/components/ui/switch';
import { MCMService } from '@/services/mcmService';
import { routePathByName } from '@/app/routes';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { getAsset as getAssetById } from '@/services/assetsService';
import { useUser } from '@/contexts/UserContextProvider';
import { saveUserInfo } from '@/services/userService';
import MCMGraphDialog from '@/components/mcm/MCMGraphDialog';
import companyService from '@/services/companyService';

export type MappingLink = {
    id: string;
    ref?: string;
    title?: string;
    domain?: string;
    relation?: string;
};

export interface MCMMappingsCardProps {
    tenancyId: string | undefined;
    isReadOnly?: boolean;
    controlIds: string[];
    defaultEntityLink?: MappingLink; // Used to pre-select current entity (Risk/Asset)
    onEnsureControlLinked?: (controlId: string) => Promise<void> | void; // Called when master control changes / Apply
    className?: string;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    persistMode?: 'remote' | 'staged';
    onApplyStaged?: (payload: { controlId: string; links: MappingLink[]; standard?: string }) => void;
    pendingEntityLink?: MappingLink;
    maxWidth?: number | string;
}

export const MCMMappingsCard: React.FC<MCMMappingsCardProps> = ({
    tenancyId,
    isReadOnly,
    controlIds,
    defaultEntityLink,
    onEnsureControlLinked,
    className,
    collapsible,
    defaultCollapsed,
    persistMode = 'remote',
    onApplyStaged,
    pendingEntityLink,
    maxWidth,
}) => {
    const theme = useTheme();
    const { user } = useUser();
    const userPrefDirectMode = useMemo(() => {
        try {
            const u: any = Array.isArray(user) ? user[0] : undefined;
            const val = u?.mcmDirectMode;
            return typeof val === 'boolean' ? val : undefined;
        } catch {
            return undefined;
        }
    }, [user]);
    const storagePrefDirectMode = useMemo(() => {
        try {
            const v = typeof window !== 'undefined' ? window.localStorage.getItem('mcmDirectMode') : null;
            if (v === '1' || v === 'true') return true;
            if (v === '0' || v === 'false') return false;
            return undefined;
        } catch {
            return undefined;
        }
    }, []);

    const isMcmCardFeatureEnabled = useMemo(() => {
        try {
            const features = getAvailableFeatures();
            return features.some((f) => f.name === 'MCM Mapping Card');
        } catch {
            return true;
        }
    }, []);

    // Check if MCM migration has been completed
    const [mcmMigrationCompleted, setMcmMigrationCompleted] = useState<boolean | null>(null);
    
    useEffect(() => {
        const checkMigrationStatus = async () => {
            try {
                const settings = await companyService.getCompanySettings(undefined, "features");
                setMcmMigrationCompleted(settings?.mcmMigrationCompleted ?? false);
            } catch (error) {
                console.error('[MCMMappingsCard] Failed to check MCM migration status:', error);
                setMcmMigrationCompleted(false);
            }
        };
        
        if (isMcmCardFeatureEnabled && tenancyId) {
            checkMigrationStatus();
        }
    }, [isMcmCardFeatureEnabled, tenancyId]);

    // Only show the card if feature is enabled AND migration is completed
    const isMcmCardEnabled = isMcmCardFeatureEnabled && mcmMigrationCompleted === true;

    

    const [mcmMasterControl, setMcmMasterControl] = useState<{ id: string; label: string } | null>(null);
    const [mcmLinksByControl, setMcmLinksByControl] = useState<Record<string, any[]>>({});
    const [mcmControlMeta, setMcmControlMeta] = useState<Record<string, { label: string; standard?: string }>>({});
    const [mcmLoading, setMcmLoading] = useState(false);
    const [mcmSaving, setMcmSaving] = useState(false);
    const [mcmMasterStandard, setMcmMasterStandard] = useState<string | undefined>(undefined);
    const [mcmInitialized, setMcmInitialized] = useState(false);
    // Default to collapsed on initial load unless explicitly overridden by prop
    const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed ?? true);
    const [domainExpanded, setDomainExpanded] = useState<Record<string, boolean>>({});
    const [standardExpanded, setStandardExpanded] = useState<Record<string, boolean>>({});
    const [dirtyControls, setDirtyControls] = useState<Record<string, boolean>>({});
    const [componentLinks, setComponentLinks] = useState<any[]>([]);
    const [componentSaving, setComponentSaving] = useState(false);
    const [componentDirty, setComponentDirty] = useState(false);
    const [directMode, setDirectMode] = useState<boolean>(userPrefDirectMode ?? storagePrefDirectMode ?? true);
    const [modeDirty, setModeDirty] = useState(false);
    const [graphOpen, setGraphOpen] = useState(false);

    useEffect(() => {
        if (userPrefDirectMode !== undefined && !modeDirty) {
            setDirectMode(userPrefDirectMode);
        }
    }, [userPrefDirectMode, modeDirty]);

    const handleToggleMode = useCallback((v: boolean) => {
        const next = !!v;
        setDirectMode(next);
        setModeDirty(true);
        try {
            void saveUserInfo({ mcmDirectMode: next });
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('mcmDirectMode', next ? '1' : '0');
            }
        } catch {}
    }, []);
    const [titleCache, setTitleCache] = useState<Record<string, string>>({});

    // Discovery of control IDs that link to the provided default entity
    const [discoveredControlIds, setDiscoveredControlIds] = useState<string[]>([]);
    const effectiveControlIds = useMemo(
        () => Array.from(new Set([...(controlIds || []).map(String), ...discoveredControlIds])),
        [controlIds, discoveredControlIds]
    );
    const controlIdsKey = useMemo(() => effectiveControlIds.map(String).sort().join(','), [effectiveControlIds]);

    useEffect(() => {
        const discover = async () => {
            try {
                if (!isMcmCardEnabled) {
                    setDiscoveredControlIds([]);
                    return;
                }
                if (!tenancyId || !defaultEntityLink) {
                    setDiscoveredControlIds([]);
                    return;
                }
                const resp = await MCMService.getControls(false, tenancyId);
                const found = new Set<string>();
                const entityDomain = String(defaultEntityLink.domain || '').toLowerCase();
                const entityRef = String(defaultEntityLink.ref || '').trim();
                const entityId = String(defaultEntityLink.id || '').trim();
                const entityIdTail = entityId.includes(':') ? entityId.split(':').pop() as string : entityId;
                const refCandidates = Array.from(new Set([
                    entityRef,
                    entityIdTail,
                    entityId,
                ].filter((s) => !!s && String(s).trim() !== '')));

                const normalizeDomain = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
                const entityDomNorm = normalizeDomain(entityDomain);

                const matchesEntity = (link: any) => {
                    const domainRaw = String(link?.domain || '');
                    const domain = domainRaw.toLowerCase();
                    const linkDomNorm = normalizeDomain(domainRaw);
                    const domainMatches = (
                        linkDomNorm === entityDomNorm ||
                        linkDomNorm.includes(entityDomNorm) ||
                        entityDomNorm.includes(linkDomNorm)
                    );
                    const ref = String(link?.ref ?? '').trim();
                    const idStr = String(link?.id ?? '').trim();
                    const matchRef = (candidate: string) => candidate !== '' && ref === candidate;
                    const matchId = (candidate: string) => {
                        if (candidate === '') return false;
                        const parts = idStr.split(':').filter(Boolean);
                        // canonical internal:domain:<ref>
                        if (parts.length === 3 && parts[0] === 'internal' && parts[2] === candidate) {
                            // accept when domain matches or is close (synonyms)
                            if (normalizeDomain(parts[1]) === entityDomNorm) return true;
                            // fallback: allow any domain if exact ref matches triple form
                            return true;
                        }
                        // older internal:<ref> fallback (only when domain matches)
                        if (parts.length === 2 && parts[0] === 'internal' && parts[1] === candidate) return true;
                        // direct id equality fallback
                        if (idStr === entityId) return true;
                        return false;
                    };
                    // Prefer domain match + ref/id equality; if domain doesn't match, still accept canonical id equality
                    if (domainMatches && refCandidates.some((c) => matchRef(c))) return true;
                    return refCandidates.some((c) => matchId(c));
                };

                // Iterate controls data structure
                for (const stdObj of (resp.Controls as any[])) {
                    for (const [key, val] of Object.entries(stdObj)) {
                        if (key === 'standard') continue;
                        const ctrl: any = val;
                        const links = Array.isArray(ctrl?.linkedComponents) ? ctrl.linkedComponents : [];
                        if (links.some(matchesEntity)) {
                            found.add(String(key));
                            continue;
                        }
                        // Legacy arrays by domain
                        if (entityDomain === 'document' || entityDomain === 'evidence') {
                            // Check both supportingDocuments and evidence arrays for document/evidence domain
                            const docs: string[] = Array.isArray(ctrl?.supportingDocuments) ? ctrl.supportingDocuments : [];
                            const evidence: string[] = Array.isArray(ctrl?.evidence) ? ctrl.evidence : [];
                            const allDocs = [...docs, ...evidence];
                            if (allDocs.includes(entityRef) || allDocs.includes(entityId)) found.add(String(key));
                        } else if (entityDomain === 'assurance') {
                            const tasks: string[] = Array.isArray(ctrl?.tasks) ? ctrl.tasks.map(String) : [];
                            if (tasks.includes(entityRef) || tasks.includes(entityId)) found.add(String(key));
                        } else if (entityDomain === 'asset') {
                            const assets: string[] = Array.isArray(ctrl?.assets) ? ctrl.assets.map(String) : [];
                            if (assets.includes(entityRef) || assets.includes(entityId)) found.add(String(key));
                        } else if (entityDomain === 'policy') {
                            const policies: string[] = Array.isArray(ctrl?.policies) ? ctrl.policies.map(String) : [];
                            if (policies.includes(entityRef) || policies.includes(entityId)) found.add(String(key));
                        } else if (entityDomain === 'risk') {
                            const risks: string[] = Array.isArray(ctrl?.risks) ? ctrl.risks.map(String) : [];
                            if (risks.includes(entityRef) || risks.includes(entityId)) found.add(String(key));
                        }
                    }
                }

                setDiscoveredControlIds(Array.from(found));
            } catch (e) {
                console.warn('[MCMMappingsCard] discovery failed', e);
                setDiscoveredControlIds([]);
            }
        };

        // Always try to augment with discovery when entity is known
        discover();
    }, [isMcmCardEnabled, tenancyId, defaultEntityLink?.id, defaultEntityLink?.ref, defaultEntityLink?.domain]);

    useEffect(() => {
        const loadMappings = async () => {
            if (!isMcmCardEnabled) {
                return;
            }
            if (!tenancyId || effectiveControlIds.length === 0) {
                if (mcmInitialized) {
                    setMcmLinksByControl({});
                    setMcmControlMeta({});
                    setMcmMasterControl(null);
                    setMcmInitialized(false);
                }
                return;
            }

            if (!mcmInitialized) setMcmLoading(true);
            // Start from current local state to preserve any dirty entries
            const linksMap: Record<string, any[]> = { ...mcmLinksByControl };
            const metaMap: Record<string, { label: string; standard?: string }> = {};

            for (const cid of effectiveControlIds) {
                try {
                    const ctrl = await MCMService.getControlById(cid, tenancyId);
                    let links = Array.isArray((ctrl as any)?.linkedComponents) ? (ctrl as any).linkedComponents : [];

                    if (defaultEntityLink) {
                        const hasEntity = links.some((lnk: any) => lnk.id === defaultEntityLink.id || lnk.ref === defaultEntityLink.ref);
                        if (!hasEntity) links = [...links, defaultEntityLink];
                    }
                    if (pendingEntityLink) {
                        const hasPending = links.some((lnk: any) => lnk.id === pendingEntityLink.id || lnk.ref === pendingEntityLink.ref);
                        if (!hasPending) links = [...links, pendingEntityLink];
                    }
                    if (!dirtyControls[cid]) {
                        linksMap[cid] = links;
                    }
                    const label = (ctrl as any)?.controlName || (ctrl as any)?.controlID || cid;
                    const standard = (ctrl as any)?.standard;
                    metaMap[cid] = { label: String(label), standard: standard ? String(standard) : undefined };
                } catch (e) {
                    if (!dirtyControls[cid]) {
                        linksMap[cid] = defaultEntityLink ? [defaultEntityLink] : [];
                    }
                    metaMap[cid] = { label: cid };
                }
            }

            setMcmLinksByControl(linksMap);
            setMcmControlMeta(metaMap);
            setMcmLoading(false);
            setMcmInitialized(true);
        };

        loadMappings();
    }, [isMcmCardEnabled, tenancyId, controlIdsKey, defaultEntityLink?.id, defaultEntityLink?.ref, pendingEntityLink?.id, pendingEntityLink?.ref, JSON.stringify(dirtyControls)]);

    // Load existing links for the current entity (component-primary persistence) if an entity is provided
    useEffect(() => {
        let mounted = true;
        const loadComponentLinks = async () => {
            try {
                if (!tenancyId || !defaultEntityLink) {
                    if (mounted && !componentDirty) setComponentLinks([]);
                    return;
                }
                const refRaw = String(defaultEntityLink.ref || '').trim();
                const idTail = String(defaultEntityLink.id || '').split(':').pop() as string;
                const compId = refRaw || idTail;
                const domain = String(defaultEntityLink.domain || 'Component');
                const comp = await MCMService.getComponentById(domain, compId, tenancyId);
                const links = Array.isArray((comp as any)?.linkedComponents) ? (comp as any).linkedComponents : [];
                if (mounted && !componentDirty) setComponentLinks(links);
            } catch (e) {
                if (mounted && !componentDirty) setComponentLinks([]);
            }
        };
        loadComponentLinks();
        return () => { mounted = false; };
    }, [tenancyId, defaultEntityLink?.id, defaultEntityLink?.ref, defaultEntityLink?.domain, componentDirty]);

    // Keep pending entity link title in sync across local links when task name changes
    useEffect(() => {
        if (!pendingEntityLink || (!pendingEntityLink.id && !pendingEntityLink.ref)) return;
        setMcmLinksByControl((prev) => {
            let changed = false;
            const next: Record<string, any[]> = {};
            for (const cid of Object.keys(prev)) {
                const list = Array.isArray(prev[cid]) ? prev[cid] : [];
                let updated = false;
                const newList = list.map((lnk: any) => {
                    const match = (pendingEntityLink.id && lnk?.id === pendingEntityLink.id) || (pendingEntityLink.ref && lnk?.ref === pendingEntityLink.ref);
                    if (match) {
                        updated = true;
                        return {
                            ...lnk,
                            title: pendingEntityLink.title ?? lnk.title,
                            domain: pendingEntityLink.domain ?? lnk.domain,
                            relation: pendingEntityLink.relation ?? lnk.relation,
                        };
                    }
                    return lnk;
                });
                next[cid] = updated ? newList : list;
                if (updated) changed = true;
            }
            return changed ? next : prev;
        });
    }, [pendingEntityLink?.id, pendingEntityLink?.ref, pendingEntityLink?.title]);

    const handleApply = useCallback(async () => {
        if (!tenancyId || !mcmMasterControl) return;
        const links = Array.isArray(mcmLinksByControl[mcmMasterControl.id]) ? mcmLinksByControl[mcmMasterControl.id] : [];
        if (links.length === 0) return;
        if (persistMode === 'staged' && onApplyStaged) {
            onApplyStaged({ controlId: mcmMasterControl.id, links, standard: mcmMasterStandard || mcmControlMeta[mcmMasterControl.id]?.standard });
            return;
        }
        setMcmSaving(true);
        try {
            if (onEnsureControlLinked) await onEnsureControlLinked(mcmMasterControl.id);

            const resolvedStd = mcmMasterStandard || mcmControlMeta[mcmMasterControl.id]?.standard;
            await MCMService.bulkUpdate(
                {
                    controlIds: [String(mcmMasterControl.id)],
                    type: 'linkedComponents',
                    action: 'update',
                    data: { linkedComponents: links },
                    standard: resolvedStd,
                },
                tenancyId
            );
            // Clear dirty flag after successful persist
            setDirtyControls((prev) => ({ ...prev, [mcmMasterControl.id]: false }));
        } finally {
            setMcmSaving(false);
        }
    }, [tenancyId, mcmMasterControl, mcmLinksByControl, mcmControlMeta, mcmMasterStandard, onEnsureControlLinked, persistMode, onApplyStaged]);

    const handleApplyComponent = useCallback(async () => {
        if (!tenancyId || !defaultEntityLink) return;
        const links = Array.isArray(componentLinks) ? componentLinks : [];
        if (links.length === 0) return;
        setComponentSaving(true);
        try {
            const domain = String(defaultEntityLink.domain || 'Component');
            const refRaw = String(defaultEntityLink.ref || '').trim();
            const idTail = String(defaultEntityLink.id || '').split(':').pop() as string;
            const compId = refRaw || idTail;
            await MCMService.updateComponentLinks(tenancyId, domain, compId, links, { title: defaultEntityLink.title });
            for (const lnk of links) {
                const tDom = String(lnk?.domain || 'Component');
                const tRef = String(lnk?.ref || '').trim();
                const tIdTail = String(lnk?.id || '').split(':').pop() as string;
                const targetId = tRef || tIdTail;
                if (!targetId) continue;
                if (tDom.toLowerCase() === domain.toLowerCase() && targetId === compId) continue;
                const target = await MCMService.getComponentById(tDom, targetId, tenancyId);
                const existing = Array.isArray((target as any)?.linkedComponents) ? (target as any).linkedComponents : [];
                const originDom = String(defaultEntityLink.domain || 'Component');
                const reverse = {
                    id: String(defaultEntityLink.id || ''),
                    ref: String(defaultEntityLink.ref || ''),
                    title: defaultEntityLink.title,
                    domain: originDom,
                    relation: MCMService.getRelationForDomain(originDom),
                    confidence: 0.7,
                } as any;
                const has = existing.some((x: any) => (reverse.id && x?.id === reverse.id) || (reverse.ref && x?.ref === reverse.ref));
                const next = has ? existing : [...existing, reverse];
                await MCMService.updateComponentLinks(tenancyId, tDom, targetId, next);
            }
            setComponentDirty(false);
        } finally {
            setComponentSaving(false);
        }
    }, [tenancyId, defaultEntityLink?.id, defaultEntityLink?.ref, defaultEntityLink?.domain, defaultEntityLink?.title, componentLinks]);

    // Helper to color code domain dot
    const dotClassForDomain = (domainRaw: string) => {
        const d = String(domainRaw || '').toLowerCase();
        if (d === 'asset') return 'bg-amber-400/70';
        if (d === 'policy') return 'bg-indigo-400/70';
        if (d === 'document') return 'bg-teal-400/70';
        if (d === 'risk') return 'bg-rose-400/70';
        if (d === 'assurance') return 'bg-violet-400/70';
        if (d === 'incident') return 'bg-orange-400/70';
        if (d === 'control') return 'bg-blue-400/70';
        return 'bg-primary/60';
    };

    const displayNameFor = (domainRaw?: string, title?: string, ref?: string, id?: string) => {
        const d = String(domainRaw || '').toLowerCase();
        const t = String(title || '').trim();
        const isAssurance = d === 'assurance';
        const generic = isAssurance && (t === '' || t.toLowerCase() === 'assurance task');
        if (generic) return ref || id || t;
        // Prefer fetched friendly title for Assets/Suppliers when present
        if (d === 'asset' || d === 'supplier') {
            const idTail = (() => {
                const full = String(id || ref || '').trim();
                if (!full) return '';
                return full.includes(':') ? full.split(':').pop() as string : full;
            })();
            const key = `${d === 'supplier' ? 'Supplier' : 'Asset'}:${idTail}`;
            if (titleCache[key]) return titleCache[key];
        }
        return t || ref || id || '';
    };

    const idFrom = (obj: { id?: string; ref?: string }) => {
        const ref = String(obj.ref || '').trim();
        if (ref) return ref;
        const id = String(obj.id || '').trim();
        if (!id) return '';
        if (id.includes(':')) return id.split(':').pop() as string;
        return id;
    };

    const pathForItem = (it: AggregatedItem): string | null => {
        const dom = String(it.domain || '').toLowerCase().replace(/[^a-z]/g, '');
        const id = idFrom({ id: it.id, ref: it.ref });
        switch (dom) {
            case 'asset':
            case 'supplier':
                return id ? routePathByName('asset.detail', { id }) : null;
            case 'risk':
                return id ? routePathByName('risk.detail', { id }) : null;
            case 'document':
            case 'evidence':
                return id ? routePathByName('documentdetail', { id }) : null;
            case 'assurance': {
                const base = routePathByName('assurance-library');
                return id ? `${base}?assurance=${encodeURIComponent(id)}` : base;
            }
            case 'correctiveaction': {
                const base = routePathByName('auditcentre');
                return id ? `${base}?tab=corrective-actions&id=${encodeURIComponent(id)}` : base;
            }
            case 'policy':
                return id ? routePathByName('policy-admin.edit', { policyId: id }) : routePathByName('policy-admin');
            case 'finding':
                return id ? `${routePathByName('auditcentre')}?tab=nonconformity&id=${encodeURIComponent(id)}` : routePathByName('auditcentre');
            case 'incident':
                return id ? `${routePathByName('auditcentre')}?tab=incidents&id=${encodeURIComponent(id)}` : routePathByName('auditcentre');
            case 'audit':
                return id ? `${routePathByName('auditcentre')}?tab=audit&id=${encodeURIComponent(id)}` : routePathByName('auditcentre');
            case 'control':
                return id ? `${routePathByName('soa')}?control=${encodeURIComponent(id)}` : routePathByName('soa');
            case 'checklist':
                return id ? routePathByName('checklists.item', { id }) : routePathByName('checklists');
            case 'privacyrequest':
                return id ? routePathByName('privacy-request-register.item', { id }) : routePathByName('privacy-request-register');
            case 'datatype':
            case 'datastoragelocation':
                return id ? routePathByName('dataRegister.item', { id }) : routePathByName('dataRegister');
            case 'legalobligation':
                return id ? routePathByName('msg.context.legal.item', { id }) : routePathByName('msg.context.legal');
            case 'interestedparty':
                return id ? routePathByName('msg.context.parties.item', { id }) : routePathByName('msg.context.parties');
            case 'objective':
                return id ? routePathByName('msg.planning.objectives.item', { id }) : routePathByName('msg.planning.objectives');
            case 'issue':
                return id ? routePathByName('msg.context.issues.item', { id }) : routePathByName('msg.context.issues');
            default:
                return null;
        }
    };

    const handleOpenItem = useCallback((it: AggregatedItem) => {
        const href = pathForItem(it);
        if (!href) return;
        try {
            window.open(href, '_blank', 'noopener,noreferrer');
        } catch {}
    }, []);

    type AggregatedItem = {
        id?: string;
        ref?: string;
        title?: string;
        domain?: string;
        relations: string[];
        controls: { id: string; label: string; standard?: string }[];
    };

    const groupedByDomain = useMemo(() => {
        const map: Record<string, Record<string, AggregatedItem>> = {};
        if (!directMode) {
            for (const cid of effectiveControlIds) {
                const links = Array.isArray(mcmLinksByControl[cid]) ? mcmLinksByControl[cid] : [];
                for (const lnk of links) {
                    const dom = String(lnk?.domain || 'Other');
                    const key = String(lnk?.id || lnk?.ref || `${dom}:${lnk?.title || ''}`);
                    if (!map[dom]) map[dom] = {};
                    const current = map[dom][key] || {
                        id: lnk?.id,
                        ref: lnk?.ref,
                        title: lnk?.title,
                        domain: dom,
                        relations: [],
                        controls: [],
                    };
                    const curTitle = String(current.title || '').trim();
                    const lnkTitle = String(lnk?.title || '').trim();
                    const isAssurance = dom.toLowerCase() === 'assurance';
                    const curGeneric = isAssurance && (curTitle === '' || curTitle.toLowerCase() === 'assurance task');
                    const newGeneric = isAssurance && (lnkTitle === '' || lnkTitle.toLowerCase() === 'assurance task');
                    if (isAssurance) {
                        if (curGeneric && !newGeneric && lnkTitle) {
                            (current as any).title = lnkTitle;
                        }
                    } else {
                        if (!curTitle && lnkTitle) {
                            (current as any).title = lnkTitle;
                        }
                    }
                    const rel = String(lnk?.relation || '').trim();
                    if (rel && !current.relations.includes(rel)) current.relations.push(rel);
                    const meta = mcmControlMeta[cid] || { label: cid };
                    if (!current.controls.some(c => c.id === cid)) {
                        current.controls.push({ id: cid, label: meta.label, standard: meta.standard });
                    }
                    map[dom][key] = current;
                }
            }
        }
        for (const lnk of (Array.isArray(componentLinks) ? componentLinks : [])) {
            const dom = String(lnk?.domain || 'Other');
            const key = String(lnk?.id || lnk?.ref || `${dom}:${lnk?.title || ''}`);
            if (!map[dom]) map[dom] = {};
            const current = map[dom][key] || {
                id: lnk?.id,
                ref: lnk?.ref,
                title: lnk?.title,
                domain: dom,
                relations: [],
                controls: [],
            };
            const curTitle = String(current.title || '').trim();
            const lnkTitle = String(lnk?.title || '').trim();
            const isAssurance = dom.toLowerCase() === 'assurance';
            const curGeneric = isAssurance && (curTitle === '' || curTitle.toLowerCase() === 'assurance task');
            const newGeneric = isAssurance && (lnkTitle === '' || lnkTitle.toLowerCase() === 'assurance task');
            if (isAssurance) {
                if (curGeneric && !newGeneric && lnkTitle) {
                    (current as any).title = lnkTitle;
                }
            } else {
                if (!curTitle && lnkTitle) {
                    (current as any).title = lnkTitle;
                }
            }
            const rel = String(lnk?.relation || '').trim();
            if (rel && !current.relations.includes(rel)) current.relations.push(rel);
            map[dom][key] = current;
        }
        const domains = Object.keys(map);
        const nonControl = domains.filter(d => d !== 'Control');
        nonControl.sort((a, b) => a.localeCompare(b));
        const ordered = [...nonControl, ...(domains.includes('Control') ? ['Control'] : [])];
        return ordered.map(domain => ({ domain, items: Object.values(map[domain]) }));
    }, [effectiveControlIds, mcmLinksByControl, mcmControlMeta, componentLinks, directMode]);

    // Aggregate all contributing controls across all grouped items, grouped by standard
    const controlsByStandard = useMemo(() => {
        const grouped: Record<string, { id: string; label: string; standard?: string }[]> = {};
        const seen = new Set<string>();
        for (const group of groupedByDomain) {
            for (const it of group.items) {
                for (const c of it.controls) {
                    if (seen.has(c.id)) continue;
                    seen.add(c.id);
                    const key = c.standard || '—';
                    (grouped[key] ||= []).push(c);
                }
            }
        }
        const entries = Object.entries(grouped).map(([standard, controls]) => ({
            standard,
            controls: controls.sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id)),
        })).sort((a, b) => a.standard.localeCompare(b.standard));
        const total = entries.reduce((acc, g) => acc + g.controls.length, 0);
        return { entries, total };
    }, [groupedByDomain]);

    const graphControls = useMemo(() => {
        const out: { id: string; label?: string; standard?: string }[] = [];
        const seen = new Set<string>();
        for (const cid of effectiveControlIds) {
            const id = String(cid);
            if (seen.has(id)) continue;
            seen.add(id);
            const meta = mcmControlMeta[id] || {} as any;
            out.push({ id, label: meta.label, standard: meta.standard });
        }
        return out;
    }, [effectiveControlIds, mcmControlMeta]);

    const graphItems = useMemo(() => {
        const out: { id?: string; ref?: string; title?: string; domain?: string }[] = [];
        const seen = new Set<string>();
        const add = (lnk: any) => {
            const dom = String(lnk?.domain || 'Other');
            const raw = String(lnk?.id || lnk?.ref || '').trim() || String(lnk?.title || '').trim();
            if (!raw) return;
            const tail = raw.includes(':') ? (raw.split(':').pop() as string) : raw;
            if (!tail) return;
            const key = `${dom}:${tail}`;
            if (defaultEntityLink && ((defaultEntityLink.id && lnk?.id === defaultEntityLink.id) || (defaultEntityLink.ref && lnk?.ref === defaultEntityLink.ref))) return;
            if (seen.has(key)) return;
            seen.add(key);
            out.push({ id: lnk?.id, ref: lnk?.ref, title: lnk?.title, domain: lnk?.domain });
        };
        // Business view: only component-level links
        if (directMode) {
            for (const lnk of (Array.isArray(componentLinks) ? componentLinks : [])) add(lnk);
        } else {
            // Compliance view: include both control-derived and component-level links
            for (const cid of effectiveControlIds) {
                const links = Array.isArray(mcmLinksByControl[cid]) ? mcmLinksByControl[cid] : [];
                for (const lnk of links) add(lnk);
            }
            for (const lnk of (Array.isArray(componentLinks) ? componentLinks : [])) add(lnk);
        }
        return out;
    }, [effectiveControlIds, mcmLinksByControl, componentLinks, defaultEntityLink?.id, defaultEntityLink?.ref, directMode]);

    // Enrich Asset/Supplier items missing titles by fetching asset friendly names
    useEffect(() => {
        const wanted: { key: string; id: string }[] = [];
        try {
            for (const group of groupedByDomain) {
                if (group.domain !== 'Asset' && group.domain !== 'Supplier') continue;
                for (const it of group.items) {
                    const idStr = String(it.id || it.ref || '').trim();
                    const idTail = idStr.includes(':') ? (idStr.split(':').pop() as string) : idStr;
                    if (!idTail) continue;
                    const key = `${group.domain}:${idTail}`;
                    const ttl = String(it.title || '').trim();
                    const looksLikeId = !ttl || ttl === idTail || ttl === String(it.ref || '');
                    if (!titleCache[key] && looksLikeId) wanted.push({ key, id: idTail });
                }
            }
        } catch {}
        if (wanted.length === 0) return;
        let cancelled = false;
        (async () => {
            const map = new Map<string, string>();
            for (const w of wanted) if (!map.has(w.key)) map.set(w.key, w.id);
            const tasks: Promise<void>[] = [];
            for (const [key, id] of map.entries()) {
                tasks.push((async () => {
                    try {
                        const asset: any = await getAssetById(id);
                        const ent: any = asset?.entity || asset;
                        const nm: string = String(ent?.assetDesc || ent?.assetName || ent?.name || '').trim();
                        if (!cancelled && nm) {
                            setTitleCache(prev => (prev[key] ? prev : { ...prev, [key]: nm }));
                        }
                    } catch {}
                })());
            }
            await Promise.allSettled(tasks);
        })();
        return () => { cancelled = true; };
    }, [groupedByDomain, titleCache]);

    if (!isMcmCardEnabled) return null;

    return (
        <div
            className={cn("glass-card p-0", className)}
            style={{
                width: '100%',
                maxWidth: typeof maxWidth === 'undefined' ? 1100 : maxWidth,
                backgroundColor: theme.isDark ? '#18181b' : 'white',
                color: theme.isDark ? '#f3f4f6' : '#18181b',
                border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
                boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)',
                marginTop: 24,
                // padding: 0,
                paddingBottom: 0,
                minHeight: 'auto',
            }}
        >
            <div className={cn("header-row flex flex-row items-center justify-between px-3", collapsed ? "py-1 h-9" : "py-2")}>
                <div>
                    <h3 className="section-title m-0 leading-tight">Master Control Mappings</h3>
                    {!collapsed && (
                      <div className="subtitle">Linked components for related controls</div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {(collapsible !== false) && (
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            aria-expanded={!collapsed}
                            aria-label={collapsed ? 'Expand mappings' : 'Collapse mappings'}
                        >
                            <ChevronDown className={cn("h-4 w-4 transition-transform", collapsed ? "-rotate-90" : "rotate-0")} />
                        </button>
                    )}
                </div>
            </div>
            {!collapsed && (
                <div className="">
                    {/* Toolbar inside expanded content: view switch + visualise icon */}
                    <div className="px-3 py-2 mb-2 border-none flex items-center gap-3">
                        <div className="ml-auto flex items-center gap-2">
                            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                                <span className={cn("transition-colors", !directMode ? "text-foreground" : "")}>Compliance View</span>
                                <Switch checked={directMode} onCheckedChange={handleToggleMode} disabled={!defaultEntityLink} />
                                <span className={cn("transition-colors", directMode ? "text-foreground" : "")}>Business View</span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setGraphOpen(true)}
                              className="backdrop-blur-md bg-white/80 hover:bg-white dark:bg-secondary/90 dark:hover:bg-card/90 transition-all duration-200 ease-in-out shadow-sm hover:shadow-lg hover:shadow-primary/30 hover:scale-105 text-gray-800 dark:text-gray-100"
                              aria-label="Visualise"
                              disabled={!defaultEntityLink}
                              type="button"
                            >
                              <i className="fa-duotone fa-solar-system text-base" aria-hidden="true"></i>
                              <span className="sr-only">Visualise</span>
                            </Button>
                        </div>
                    </div>
                    <div className="mb-4">
                        {directMode && defaultEntityLink ? (
                            <>
                                <div className="text-sm font-semibold text-foreground mb-2">Mappings for {displayNameFor(defaultEntityLink.domain, defaultEntityLink.title, defaultEntityLink.ref, defaultEntityLink.id)}</div>
                                <MCMCombobox
                                    selected={Array.isArray(componentLinks) ? componentLinks : []}
                                    onChange={(links) => {
                                        setComponentLinks(links);
                                        setComponentDirty(true);
                                    }}
                                    requirePrimary={false}
                                    pinnedEntity={{
                                        id: defaultEntityLink.id,
                                        ref: defaultEntityLink.ref,
                                        title: defaultEntityLink.title,
                                        domain: defaultEntityLink.domain,
                                    }}
                                    fullWidth
                                />
                                <div className="flex justify-end mt-2">
                                    <Button
                                        onClick={handleApplyComponent}
                                        disabled={
                                            !!isReadOnly ||
                                            componentSaving ||
                                            !tenancyId ||
                                            !Array.isArray(componentLinks) ||
                                            componentLinks.length === 0
                                        }
                                        className="backdrop-blur-sm"
                                    >
                                        {componentSaving ? 'Saving...' : 'Apply to Component'}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <MCMCombobox
                                    selected={mcmMasterControl && mcmLinksByControl[mcmMasterControl.id] ? mcmLinksByControl[mcmMasterControl.id] : []}
                                    onChange={(links) => {
                                        if (!mcmMasterControl) return;
                                        setMcmLinksByControl((prev) => ({ ...prev, [mcmMasterControl.id]: links }));
                                        setDirtyControls((prev) => ({ ...prev, [mcmMasterControl.id]: true }));
                                    }}
                                    initialMasterControl={mcmMasterControl}
                                    onMasterChange={async (ctrl) => {
                                        setMcmMasterControl(ctrl);
                                        if (ctrl?.id && onEnsureControlLinked) {
                                            await onEnsureControlLinked(ctrl.id);
                                        }
                                        if (ctrl?.id) {
                                            const std = mcmControlMeta[ctrl.id]?.standard;
                                            setMcmMasterStandard(std);
                                            setMcmLinksByControl((prev) => {
                                                const current = Array.isArray(prev[ctrl.id]) ? prev[ctrl.id] : [];
                                                const next = [...current];
                                                const ensureHas = (lnk?: MappingLink) => {
                                                    if (!lnk) return;
                                                    const exists = next.some((x) => x.id === lnk.id || (!!x.ref && x.ref === lnk.ref));
                                                    if (!exists) next.push(lnk);
                                                };
                                                ensureHas(defaultEntityLink);
                                                ensureHas(pendingEntityLink);
                                                return { ...prev, [ctrl.id]: next };
                                            });
                                            setDirtyControls((prev) => ({ ...prev, [ctrl.id]: true }));
                                            setDiscoveredControlIds((prev) => Array.from(new Set([...(prev || []), ctrl.id])));
                                        }
                                    }}
                                    requirePrimary={true}
                                    targetControlIds={effectiveControlIds}
                                    targetControls={effectiveControlIds.map((id) => ({ id, label: mcmControlMeta[id]?.label || id }))}
                                    fullWidth
                                />
                                <div className="flex justify-end mt-2">
                                    <Button
                                        onClick={handleApply}
                                        disabled={
                                            !!isReadOnly ||
                                            mcmSaving ||
                                            !tenancyId ||
                                            !mcmMasterControl ||
                                            !Array.isArray(mcmLinksByControl[mcmMasterControl.id]) ||
                                            mcmLinksByControl[mcmMasterControl.id].length === 0
                                        }
                                        className="backdrop-blur-sm"
                                    >
                                        {mcmSaving ? 'Saving...' : 'Apply to MCM'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                    <div>
                        {mcmLoading ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400">Loading mappings...</div>
                        ) : groupedByDomain.length === 0 ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400">No mappings found.</div>
                        ) : (
                            <div className="space-y-5">
                                {groupedByDomain.map(group => {
                                    const items = group.items;
                                    const domain = group.domain;
                                    const groupLabel = (
                                        domain === 'Policy' ? 'Policies' :
                                            domain === 'Document' ? 'Documents' :
                                                domain === 'Asset' ? 'Assets' :
                                                    domain === 'Risk' ? 'Risks' :
                                                        domain === 'Assurance' ? 'Assurance Tasks' :
                                                            domain === 'Control' ? 'Controls' : domain
                                    );
                                    return (
                                        <div key={domain} className="p-3 backdrop-blur-sm rounded-xl dark:bg-white/5 dark:border dark:border-white/10 shadow-sm dark:shadow-none">
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-semibold text-foreground">{groupLabel}</div>
                                                {/* Removed item count badge per DED-1438 */}
                                            </div>
                                            {(() => {
                                                const isExpanded = !!domainExpanded[domain];
                                                const visibleItems = isExpanded ? items : items.slice(0, 4);
                                                return (
                                                    <>
                                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                                            {visibleItems.map((it) => {
                                                                const titleText = displayNameFor(it.domain, it.title, it.ref, it.id);
                                                                return (
                                                                    <HoverCard key={(it.id || it.ref || titleText || Math.random()).toString()}>
                                                                        <HoverCardTrigger asChild>
                                                                            <div
                                                                                className="flex items-center gap-2 min-w-0 px-1.5 py-1 rounded-md transition-colors dark:hover:bg-white/5 hover:bg-black/5"
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                onClick={() => handleOpenItem(it)}
                                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenItem(it); } }}
                                                                            >
                                                                                <span className={"h-1.5 w-1.5 rounded-full shrink-0 " + dotClassForDomain(it.domain || '')} />
                                                                                <span
                                                                                  className="text-[12px] font-medium"
                                                                                  style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}
                                                                                >{titleText}</span>
                                                                                {/* Removed per-item controls count per DED-1438 */}
                                                                            </div>
                                                                        </HoverCardTrigger>
                                                                        <HoverCardContent className="w-80">
                                                                            <div className="text-xs font-medium mb-1">{titleText || 'Unknown'}</div>
                                                                            {/* Removed "Why this is linked" section per DED-1438 */}
                                                                            <div className="text-xs font-medium mb-1">Linked Controls</div>
                                                                            {(() => {
                                                                                const grouped = it.controls.reduce((acc: Record<string, { id: string; label: string; standard?: string }[]>, ctl: { id: string; label: string; standard?: string }) => {
                                                                                    const key = ctl.standard || '—';
                                                                                    (acc[key] ||= []).push(ctl);
                                                                                    return acc;
                                                                                }, {} as Record<string, { id: string; label: string; standard?: string }[]>);
                                                                                const entries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
                                                                                return (
                                                                                    <div className="space-y-2">
                                                                                        {entries.map(([std, ctrls]) => (
                                                                                            <div key={std}>
                                                                                                <div className="text-[11px] text-muted-foreground mb-1">
                                                                                                    Framework: {std}
                                                                                                </div>
                                                                                                <ul className="space-y-1">
                                                                                                    {ctrls.map(c => (
                                                                                                        <li key={c.id} className="text-[12px] flex items-center gap-2">
                                                                                                            <span className="font-medium">{c.id}</span>
                                                                                                            <span className="text-muted-foreground">– {c.label}</span>
                                                                                                        </li>
                                                                                                    ))}
                                                                                                </ul>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                            <div className="mt-2 text-[11px] text-muted-foreground">ID: {idFrom({ id: it.id, ref: it.ref }) || '—'}</div>
                                                                        </HoverCardContent>
                                                                    </HoverCard>
                                                                );
                                                            })}
                                                        </div>
                                                        {items.length > 4 && (
                                                            <div className="mt-2 flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setDomainExpanded((prev) => ({ ...prev, [domain]: !isExpanded }))}
                                                                    className="text-xs text-muted-foreground hover:text-foreground"
                                                                >
                                                                    {isExpanded ? 'Show less' : 'Show all'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            {/* Contributing Controls summary */}
                            {controlsByStandard.total > 0 && (
                                <div className="p-3 backdrop-blur-sm rounded-xl dark:bg:white/5 dark:border dark:border-white/10 shadow-sm dark:shadow-none">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold text-foreground leading-none flex items-center">Linked by Controls</div>
                                        <div className="text-xs text-muted-foreground flex items-center">
                                            <span className="inline-flex items-center h-5 px-2 rounded-full dark:bg-white/5 dark:border dark:border-white/10 leading-none">
                                                {controlsByStandard.total} control{controlsByStandard.total === 1 ? '' : 's'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 space-y-2">
                                        {controlsByStandard.entries.map(({ standard, controls }) => (
                                            <div key={standard}>
                                                <div className="mb-1">
                                                    <div className="flex items-center justify-between rounded-full dark:border dark:border-white/10 dark:bg-white/5 px-2 py-0.5">
                                                        <span className="text-[11px] font-medium text-foreground">{standard || '—'}</span>
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary border border-primary/20">
                                                            {controls.length} {controls.length === 1 ? 'Control' : 'Controls'}
                                                        </span>
                                                    </div>
                                                </div>
                                                {(() => {
                                                    const isStdExpanded = !!standardExpanded[standard];
                                                    const visibleCtrls = isStdExpanded ? controls : controls.slice(0, 4);
                                                    return (
                                                        <>
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                                                {visibleCtrls.map(c => (
                                                                    <HoverCard key={c.id}>
                                                                        <HoverCardTrigger asChild>
                                                                            <div className="flex items-center gap-2 min-w-0 px-1.5 py-1 rounded-md transition-colors dark:hover:bg-white/5 hover:bg-black/5">
                                                                                <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-blue-400/70" />
                                                                                <span className="text-[12px] font-medium truncate">{c.id}</span>
                                                                                <span className="text-[12px] text-muted-foreground truncate">– {c.label}</span>
                                                                            </div>
                                                                        </HoverCardTrigger>
                                                                        <HoverCardContent className="w-80">
                                                                            <div className="text-xs font-medium mb-1">{c.id} — {c.label}</div>
                                                                            <div className="text-[11px] text-muted-foreground mb-2">Linked components</div>
                                                                            {(() => {
                                                                                const links = (Array.isArray(mcmLinksByControl[c.id]) ? (mcmLinksByControl[c.id] as MappingLink[]) : []) as MappingLink[];
                                                                                const grouped = links.reduce((acc: Record<string, MappingLink[]>, l: MappingLink) => {
                                                                                    const dom = String(l?.domain || 'Other');
                                                                                    (acc[dom] ||= []).push(l);
                                                                                    return acc;
                                                                                }, {} as Record<string, MappingLink[]>);
                                                                                const domains = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                                                                                return (
                                                                                    <div className="space-y-2">
                                                                                        {domains.map((dom) => {
                                                                                            const items = grouped[dom] || [];
                                                                                            return (
                                                                                                <div key={dom}>
                                                                                                    <div className="text-[11px] text-muted-foreground mb-1">{dom} <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full dark:bg-white/5 dark:border dark:border-white/10">{items.length}</span></div>
                                                                                                    <ul className="space-y-1">
                                                                                                        {items.map((lnk, idx) => (
                                                                                                            <li key={(lnk.id || lnk.ref || idx).toString()} className="text-[12px] flex items-center gap-2">
                                                                                                                <span className={"h-1.5 w-1.5 rounded-full shrink-0 " + dotClassForDomain(lnk.domain || '')} />
                                                                                                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{String(lnk.relation || '')}</span>
                                                                                                                <span className="text-[12px] font-medium truncate">{lnk.title || lnk.ref}</span>
                                                                                                            </li>
                                                                                                        ))}
                                                                                                    </ul>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </HoverCardContent>
                                                                    </HoverCard>
                                                                ))}
                                                            </div>
                                                            {controls.length > 4 && (
                                                                <div className="mt-1 flex justify-end">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setStandardExpanded((prev) => ({ ...prev, [standard]: !isStdExpanded }))}
                                                                        className="text-xs text-muted-foreground hover:text-foreground"
                                                                    >
                                                                        {isStdExpanded ? 'Show less' : `Show all ${controls.length}`}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <MCMGraphDialog
                open={graphOpen}
                onOpenChange={setGraphOpen}
                center={defaultEntityLink}
                items={graphItems}
                controls={graphControls}
                labelFor={(d?: string, t?: string, r?: string, i?: string) => displayNameFor(d, t, r, i)}
            />
        </div>
    );
}
